/**
 * HomeScreen — book overview surface.
 *
 * Pixel-faithful port of `HomeScreen` in `design/project/screens-home.jsx`.
 *
 * Composition:
 *   - Top bar: BookSwitcher pill (left) + search/notif/profile (right)
 *   - BalanceCard
 *   - 「近期紀錄」 section header + RecentTxList (already built)
 *
 * The screen is presentational. The parent supplies:
 *   - `activeBook`, `books`
 *   - `transactions`, `categories`
 *   - `today`, `primaryCurrency`
 *   - `onSwitchBook`, `onSearch`, `onNotif`, `onProfile`
 *
 * Data filtering happens here (active book + active month) so the component
 * is self-contained: pass the full transactions list and the screen will
 * pick out the right slice for rendering.
 *
 * Sharp edges (audit):
 *   - Empty state: BalanceCard renders zero totals (no division anywhere);
 *     RecentTxList shows its own 「本月還沒有紀錄」 placeholder.
 *   - Currencies: we collect the set present in the active month and pass
 *     them to BalanceCard; if there's only one we hide the tabs.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import Icon from '@/components/common/Icon';
import type { BookRow, CategoryRow, TransactionRow } from '@/lib/sheets/types';
import {
  endOfMonth,
  formatYmd,
  startOfMonth,
} from '@/lib/format/date';
import { dayPart } from '@/lib/format/datetime';
import { convertMinor } from '@/lib/format/fx';
import BalanceCard from './BalanceCard';
import RecentTxList from './RecentTxList';

export interface HomeScreenProps {
  activeBook: BookRow | null;
  books: BookRow[];
  transactions: TransactionRow[];
  categories: CategoryRow[];
  today: Date;
  primaryCurrency: string;
  onSwitchBook: () => void;
  onSearch: () => void;
  onNotif: () => void;
  onProfile: () => void;
  hasUnreadNotif?: boolean;
  /** Optional desktop right-rail slot (filled by App for `wide` viewport). */
  rightRail?: ReactNode;
  /** When true, render the desktop two-column variant. */
  desktopLayout?: boolean;
  onTxClick?: (tx: TransactionRow) => void;
  /** Forwarded to RecentTxList for swipe-to-delete UX. */
  onTxDelete?: (tx: TransactionRow) => void;
  onViewAll?: () => void;
}

function tokenToCss(token: string): string {
  if (token.startsWith('--')) return `var(${token})`;
  return token;
}

function inActiveMonth(t: TransactionRow, today: Date): boolean {
  if (!t.date) return false;
  const start = formatYmd(startOfMonth(today));
  const end = formatYmd(endOfMonth(today));
  // v5: `t.date` may carry `THH:MM`; compare via `dayPart` so a row on
  // the boundary day with a time suffix isn't excluded by lex order.
  const day = dayPart(t.date);
  return day >= start && day <= end;
}

/**
 * Compute the net (income − expense) for a transaction list, FX-converting
 * each entry into the primary currency. Used by the trend chip.
 */
function netInPrimary(txs: TransactionRow[], primary: string): number {
  let net = 0;
  for (const t of txs) {
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', primary);
    if (t.kind === 'income') net += minor;
    else if (t.kind === 'expense') net -= minor;
  }
  return net;
}

/**
 * Build the 「結餘較上月 +12%」 trend chip label.
 *
 * The label spells out 「結餘」 because this chip tracks month NET
 * (income − expense) while the analytics screen's compare chip tracks the
 * expense/income TOTAL of the active mode — the two legitimately move in
 * opposite directions (e.g. expenses down 45% while net flips from red to
 * black = +283%), and an unlabeled 「較上月」 read as a contradiction.
 *
 * Sharp edge: percent of zero is undefined. If the previous-month net is 0
 * (no data, brand-new book) we return `null` and the chip is hidden — the
 * design's "+12%" placeholder must NEVER leak into a real fresh-login screen.
 */
function buildTrendLabel(
  currentNet: number,
  prevNet: number,
): { label: string; direction: 'up' | 'down' } | undefined {
  if (prevNet === 0) return undefined;
  const ratio = (currentNet - prevNet) / Math.abs(prevNet);
  const pct = Math.round(ratio * 100);
  if (pct === 0) return undefined;
  const sign = pct > 0 ? '+' : '';
  return {
    label: `結餘較上月 ${sign}${pct}%`,
    direction: pct > 0 ? 'up' : 'down',
  };
}

export default function HomeScreen({
  activeBook,
  transactions,
  categories,
  today,
  primaryCurrency,
  onSwitchBook,
  onSearch,
  onNotif,
  onProfile,
  hasUnreadNotif,
  rightRail,
  desktopLayout = false,
  onTxClick,
  onTxDelete,
  onViewAll,
}: HomeScreenProps): JSX.Element {
  // Filter transactions: active book + non-deleted only.
  const bookTxs = useMemo(() => {
    const bookId = activeBook?.id;
    if (!bookId) return [] as TransactionRow[];
    return transactions.filter((t) => t.book_id === bookId && !t.deleted_at);
  }, [transactions, activeBook?.id]);

  const monthTxs = useMemo(
    () => bookTxs.filter((t) => inActiveMonth(t, today)),
    [bookTxs, today],
  );

  // Previous-month txs (for the trend chip — current month net vs last month net).
  const prevMonthTxs = useMemo(() => {
    const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return bookTxs.filter((t) => inActiveMonth(t, prev));
  }, [bookTxs, today]);

  const trend = useMemo(() => {
    const cur = netInPrimary(monthTxs, primaryCurrency);
    const prev = netInPrimary(prevMonthTxs, primaryCurrency);
    return buildTrendLabel(cur, prev);
  }, [monthTxs, prevMonthTxs, primaryCurrency]);
  const trendLabel = trend?.label;
  const trendDirection = trend?.direction;

  // Sorted full list of book transactions, newest first. The home screen
  // pages through this array in chunks (`HOME_PAGE_SIZE`) so the initial
  // render stays cheap and the user can keep scrolling for more.
  const sortedAll = useMemo(() => {
    return [...bookTxs].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? 1 : -1;
      return a.id < b.id ? 1 : -1;
    });
  }, [bookTxs]);

  const HOME_PAGE_SIZE = 30;
  const [recentLimit, setRecentLimit] = useState(HOME_PAGE_SIZE);
  // Reset the page window when the underlying tx set changes drastically
  // (book switch, large import) so the user starts at the top.
  useEffect(() => {
    setRecentLimit(HOME_PAGE_SIZE);
  }, [activeBook?.id]);

  const recent = useMemo(
    () => sortedAll.slice(0, recentLimit),
    [sortedAll, recentLimit],
  );
  const hasMore = sortedAll.length > recent.length;
  const loadMore = useCallback(() => {
    setRecentLimit((n) => Math.min(n + HOME_PAGE_SIZE, sortedAll.length));
  }, [sortedAll.length]);

  const bookName = activeBook?.name ?? '個人';
  const bookColor = activeBook?.color_token ?? '--terracotta-500';

  // ─── Top bar ─────────────────────────────────────────────────────────
  const topBar = (
    <div
      style={{
        padding: '16px 20px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <button
        type="button"
        onClick={onSwitchBook}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-1)',
          padding: '6px 12px 6px 14px',
          borderRadius: 999,
          boxShadow: 'var(--shadow-1)',
          cursor: 'pointer',
          font: '500 13px var(--font-cjk)',
          color: 'var(--fg-1)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            background: tokenToCss(bookColor),
          }}
        />
        {bookName}
        <Icon name="chevD" size={14} stroke="var(--fg-3)" />
      </button>
      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <button
          type="button"
          onClick={onSearch}
          aria-label="搜尋"
          style={iconBtnStyle}
        >
          <Icon name="search" size={20} stroke="var(--fg-2)" />
        </button>
        <button
          type="button"
          onClick={onNotif}
          aria-label="通知"
          style={{ ...iconBtnStyle, position: 'relative' }}
        >
          <Icon name="bell" size={20} stroke="var(--fg-2)" />
          {hasUnreadNotif && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 7,
                right: 8,
                width: 8,
                height: 8,
                borderRadius: 4,
                background: 'var(--accent)',
                border: '1.5px solid var(--cream-50)',
              }}
            />
          )}
        </button>
        <button
          type="button"
          onClick={onProfile}
          aria-label="帳號設定"
          style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            background: tokenToCss(bookColor),
            border: '1.5px solid var(--cream-50)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            color: '#fff',
            font: '600 13px var(--font-cjk-display)',
            marginLeft: 4,
            boxShadow: '0 1px 2px rgba(58,45,31,0.08)',
          }}
        >
          {bookName.slice(0, 1)}
        </button>
      </div>
    </div>
  );

  if (desktopLayout) {
    // Desktop main column: BalanceCard + recent activity. Right-rail slot is
    // surfaced through props (rendered by the WebShell, not here).
    return (
      <div>
        {topBar}
        <div
          style={{
            padding: '16px 32px 40px',
            display: 'grid',
            gridTemplateColumns: rightRail ? 'minmax(0, 1fr) 320px' : '1fr',
            gap: 24,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <BalanceCard
              transactions={monthTxs}
              today={today}
              currency={primaryCurrency}
              {...(trendLabel ? { trendLabel } : {})}
              {...(trendDirection ? { trendDirection } : {})}
              layout="desktop"
            />
            <RecentSection
              recent={recent}
              categories={categories}
              primaryCurrency={primaryCurrency}
              hasMore={hasMore}
              onLoadMore={loadMore}
              {...(onTxClick ? { onTxClick } : {})}
          {...(onTxDelete ? { onDelete: onTxDelete } : {})}
              {...(onViewAll ? { onViewAll } : {})}
            />
          </div>
          {rightRail && (
            <aside
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 16,
                padding: 16,
                boxShadow: 'var(--shadow-1)',
                border: '1px solid var(--border-1)',
                alignSelf: 'start',
              }}
            >
              {rightRail}
            </aside>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {topBar}
      <div style={{ padding: '8px 20px 120px' }}>
        <BalanceCard
          transactions={monthTxs}
          today={today}
          currency={primaryCurrency}
          {...(trendLabel ? { trendLabel } : {})}
          {...(trendDirection ? { trendDirection } : {})}
          layout="mobile"
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 20,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              font: '600 15px var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            近期紀錄
          </div>
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              style={{
                font: '500 12px var(--font-cjk)',
                color: 'var(--fg-3)',
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              全部 <Icon name="chevR" size={12} stroke="var(--fg-3)" />
            </button>
          )}
        </div>

        <RecentTxList
          transactions={recent}
          categories={categories}
          primaryCurrency={primaryCurrency}
          {...(onTxClick ? { onTxClick } : {})}
          {...(onTxDelete ? { onDelete: onTxDelete } : {})}
        />
        {hasMore && <LoadMoreButton onClick={loadMore} />}
      </div>
    </div>
  );
}

function LoadMoreButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          padding: '8px 18px',
          borderRadius: 999,
          background: 'var(--bg-surface)',
          border: '0.5px solid var(--border-1)',
          color: 'var(--fg-2)',
          font: '500 13px var(--font-cjk)',
          cursor: 'pointer',
        }}
      >
        載入更多
      </button>
    </div>
  );
}

function RecentSection({
  recent,
  categories,
  primaryCurrency,
  onTxClick,
  onTxDelete,
  onViewAll,
  hasMore,
  onLoadMore,
}: {
  recent: TransactionRow[];
  categories: CategoryRow[];
  primaryCurrency?: string;
  onTxClick?: (tx: TransactionRow) => void;
  /** Forwarded to RecentTxList for swipe-to-delete UX. */
  onTxDelete?: (tx: TransactionRow) => void;
  onViewAll?: () => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={{ font: '600 17px var(--font-cjk)', color: 'var(--fg-1)' }}>
          近期紀錄
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            style={{
              font: '500 13px var(--font-cjk)',
              color: 'var(--fg-2)',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            查看全部 <Icon name="chevR" size={13} stroke="var(--fg-2)" />
          </button>
        )}
      </div>
      <RecentTxList
        transactions={recent}
        categories={categories}
        {...(primaryCurrency ? { primaryCurrency } : {})}
        {...(onTxClick ? { onTxClick } : {})}
        {...(onTxDelete ? { onDelete: onTxDelete } : {})}
      />
      {hasMore && onLoadMore && <LoadMoreButton onClick={onLoadMore} />}
    </div>
  );
}

const iconBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: 18,
  background: 'transparent',
  border: 0,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
} as const;
